import React from "react";
import type { NotebookOutput } from "server/corsproxy/remoteContent/api";
import { joinNotebookSource } from "./notebookContent";

const ANSI_REGEX = /\x1b\[[0-9;]*m/g;

const stripAnsi = (text: string): string => text.replace(ANSI_REGEX, "");

export const NotebookOutputBlock: React.FC<{ output: NotebookOutput }> = ({
  output,
}) => {
  if (output.output_type === "stream") {
    return (
      <pre className="text-xs text-gray-200 whitespace-pre-wrap overflow-x-auto px-4 py-2">
        {stripAnsi(joinNotebookSource(output.text))}
      </pre>
    );
  }

  if (output.output_type === "error") {
    return (
      <pre className="text-xs text-red-400 whitespace-pre-wrap overflow-x-auto px-4 py-2">
        {stripAnsi((output.traceback || []).join("\n")) ||
          `${output.ename}: ${output.evalue}`}
      </pre>
    );
  }

  if (
    output.output_type !== "execute_result" &&
    output.output_type !== "display_data"
  ) {
    return null;
  }

  const data = output.data || {};
  if (data["image/png"] || data["image/jpeg"]) {
    const mediaType = data["image/png"] ? "png" : "jpeg";
    return (
      <div className="px-4 py-2">
        <img
          src={`data:image/${mediaType};base64,${joinNotebookSource(
            data[`image/${mediaType}`],
          )}`}
          alt="notebook output"
          className="max-w-full h-auto rounded"
        />
      </div>
    );
  }

  const embeddedContent = data["image/svg+xml"] || data["text/html"];
  if (embeddedContent) {
    return (
      <iframe
        className="h-64 w-full border-0 bg-white"
        sandbox=""
        referrerPolicy="no-referrer"
        srcDoc={joinNotebookSource(embeddedContent)}
        title={`Sandboxed ${
          data["image/svg+xml"] ? "SVG" : "HTML"
        } notebook output`}
      />
    );
  }

  if (data["text/plain"]) {
    return (
      <pre className="text-xs text-gray-200 whitespace-pre-wrap overflow-x-auto px-4 py-2">
        {stripAnsi(joinNotebookSource(data["text/plain"]))}
      </pre>
    );
  }

  return null;
};
